"use client";

/**
 * Kuttos Fashion — the brand directory ("The Houses").
 *
 * Magazine-cover grid of every ACTIVE, subscription-paid fashion house
 * (getMarketplaceBrands is gated server-side), with search and sort.
 */

import Link from "next/link";
import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Search, ArrowUpRight } from "lucide-react";

type SortKey = "newest" | "name" | "products";

export function BrandsPageClient() {
  // @ts-ignore - deep type instantiation
  const brandsData = useQuery(api.marketing.fashion.getMarketplaceBrands);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");

  const brands = ((brandsData as any)?.brands || []) as any[];
  const isLoading = brandsData === undefined;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = brands;
    if (q) {
      list = list.filter(
        (b) =>
          b.name?.toLowerCase().includes(q) ||
          b.tagline?.toLowerCase().includes(q) ||
          b.description?.toLowerCase().includes(q) ||
          b.address?.city?.toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "products") sorted.sort((a, b) => (b.totalProducts || 0) - (a.totalProducts || 0));
    // "newest" — server order is already newest-first
    return sorted;
  }, [brands, search, sort]);

  return (
    <div style={{ background: "var(--mk-paper)" }} className="min-h-screen">
      {/* Title band */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 pt-10 md:pt-14 pb-10">
        <p className="mk-eyebrow mk-rise">The directory</p>
        <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h1 className="mk-display mk-rise mk-d1" style={{ fontSize: "clamp(40px, 5.6vw, 76px)" }}>
            The <em>Houses</em>
          </h1>
          <p className="mk-rise mk-d2 max-w-md text-sm leading-relaxed lg:mb-3" style={{ color: "var(--mk-ink-soft)" }}>
            Every house on this page is verified and actively trading — when you order,
            a real atelier picks, packs and delivers.
          </p>
        </div>

        {/* Controls */}
        <div className="mk-rise mk-d3 mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--mk-ink-faint)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search houses, cities…"
              className="mk-input w-full"
              style={{ paddingLeft: 44 }}
              aria-label="Search brands"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="mk-select"
            aria-label="Sort brands"
          >
            <option value="newest">Newest houses</option>
            <option value="name">Alphabetical</option>
            <option value="products">Most pieces</option>
          </select>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 pb-20">
        <hr className="mk-rule-strong mb-10" />
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mk-skeleton" style={{ aspectRatio: "4/5" }} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div className="mk-frame p-16 text-center">
            <p className="mk-display" style={{ fontSize: 28 }}>No houses match</p>
            <p className="mt-3 text-sm" style={{ color: "var(--mk-ink-soft)" }}>
              Try a different name or clear the search.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 gap-y-8">
            {shown.map((b, i) => {
              const img = b.coverImages?.[0] || b.profileImage;
              return (
                <Link key={b._id} href={`/brands/${b.slug}`} className="mk-cover mk-rise" style={{ animationDelay: `${Math.min(i, 8) * 0.06}s` }}>
                  {img && <img src={img} alt={b.name} loading="lazy" />}
                  <div className="mk-cover__scrim" />
                  <div className="mk-cover__masthead">
                    <span>Kuttos Journal</span>
                    <span>№ {String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mk-cover__body">
                    <p className="mk-cover__name">{b.name}</p>
                    <p className="mk-cover__meta">
                      {b.totalProducts > 0 ? `${b.totalProducts} pieces` : "New house"}
                      {b.address?.city ? ` · ${b.address.city}` : ""}
                    </p>
                    {b.tagline && (
                      <p className="mt-2 text-xs italic" style={{ fontFamily: "var(--mk-serif)", color: "rgba(246,242,234,0.8)" }}>
                        “{b.tagline}”
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Seller CTA */}
        <div className="mt-16 mk-frame p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{ background: "var(--mk-paper-deep)" }}>
          <div>
            <p className="mk-eyebrow">Run a fashion house?</p>
            <p className="mk-display mt-3" style={{ fontSize: "clamp(24px, 2.6vw, 36px)" }}>
              Put your rail on <em>Kuttos</em>
            </p>
          </div>
          <a href="https://app.kuttostechnologies.com" target="_blank" rel="noopener noreferrer" className="mk-btn mk-btn--solid shrink-0">
            Open your house <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
